import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { syncSerialSelection } from "@/store/serialFilterSlice";
import { serialList } from "./state";
import type { SerialEntry } from "./types";

/**
 * Shared accessor for the app-wide serial list. The Redux cache is fetched
 * once per session (status guard below), so every page — deep-linked or
 * navigated — reads the same data without refetching. A failed first
 * attempt retries on the next mount: `error` keeps the request alive
 * instead of leaving the app without reference data for the session.
 *
 * Each loaded list is also synced into the serial filter slice (default:
 * select all until the user touches the filter) — every consumer keeps the
 * selection correct without wiring it per page.
 */
export interface UseSerialListResult {
  serials: SerialEntry[];
  loading: boolean;
  error: ReturnType<typeof serialList.selectors.selectError>;
}

export function useSerialList(): UseSerialListResult {
  const dispatch = useAppDispatch();
  const serials = useAppSelector(serialList.selectors.selectPoints);
  const loading = useAppSelector(serialList.selectors.selectLoading);
  const error = useAppSelector(serialList.selectors.selectError);
  const status = useAppSelector(serialList.selectors.selectStatus);

  useEffect(() => {
    // Fetch only while idle (or retrying an error) — `loading`/`success`
    // mean a request is in flight or the cache is already populated.
    if (status === "idle" || status === "error") {
      void dispatch(serialList.fetch());
    }
  }, [dispatch, status]);

  useEffect(() => {
    // Runs on the loaded list (and nothing else — `points` only changes on
    // a fulfilled fetch); `syncSerialSelection` is a no-op-safe re-sync.
    if (serials.length > 0) {
      dispatch(syncSerialSelection(serials.map((entry) => entry.mpKey)));
    }
  }, [dispatch, serials]);

  return { serials, loading, error };
}
